import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit';

import femtasyLocalStorage from 'helpers/femtasyLocalStorage';
import { getLanguageFromLocale } from 'helpers/locales';
import redirectToUserMarketIfNeeded from 'helpers/redirectToUserMarketIfNeeded';

import ReduxState from 'store/ReduxState';

import { setApiRequestLocale } from 'api';

export const LOGGED_OUT_USER_MARKET = 'userMarket';

const DEFAULT_MESSAGES: Record<string, string> = {};

export interface TranslationsState {
  error: SerializedError | null;
  failed: boolean;
  loading: boolean;
  locale: string;
  loaded: boolean;
  messages: Record<string, string>;
  localeSaved: boolean;
  localeIsSaving: boolean;
}

export const initialTranslationsState: TranslationsState = {
  error: null,
  failed: false,
  loading: false,
  locale: 'de',
  loaded: false,
  messages: DEFAULT_MESSAGES,
  localeSaved: false,
  localeIsSaving: false,
};

export const fetchTranslations = createAsyncThunk<
  Record<string, string>,
  string
>('translations/fetchTranslations', async (locale) => {
  const baseLocale = getLanguageFromLocale(locale);

  setApiRequestLocale(locale);

  const messages = await import(`../../lang/${baseLocale}/default.json`);

  return messages.default;
});

export const updateLocale = createAsyncThunk<
  void,
  string,
  { state: ReduxState }
>('translations/updateLocale', async (locale, { dispatch, getState }) => {
  const currentLocale = getState().translations.locale;

  await femtasyLocalStorage.setItem(LOGGED_OUT_USER_MARKET, locale);

  const { pathname } = window.location;

  if (currentLocale && currentLocale !== locale) {
    redirectToUserMarketIfNeeded({
      currentPathname: pathname,
      dispatch,
      urlLocale: currentLocale,
      userMarket: locale,
    });
  }
});

export const translationsSlice = createSlice({
  name: 'translations',
  initialState: initialTranslationsState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTranslations.pending, (state, action) => {
        state.error = null;
        state.failed = false;
        state.loading = true;
        state.loaded = false;
        state.locale = action.meta.arg;
      })
      .addCase(fetchTranslations.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.failed = false;
        state.error = null;
        state.messages = action.payload;
      })
      .addCase(fetchTranslations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
        state.failed = true;
      })
      .addCase(updateLocale.pending, (state) => {
        state.localeSaved = false;
        state.localeIsSaving = true;
        state.error = null;
      })
      .addCase(updateLocale.fulfilled, (state) => {
        state.localeIsSaving = false;
        state.localeSaved = true;
        state.error = null;
      })
      .addCase(updateLocale.rejected, (state, action) => {
        state.localeIsSaving = false;
        state.localeSaved = false;
        state.error = action.error;
      });
  },
});

export default translationsSlice.reducer;
